/**
 * ERP-FIN-02 Gate AR-02: AR Receipt Workspace  (PAY-01B enhanced)
 * Route: /ar/receipts  (ar.receipt.view)
 *
 * PAY-01B additions:
 *  - Channel-specific form sections: CASH, UPI/PhonePe (with QR), Bank Transfer
 *  - Dynamic payment methods from MDM (no hardcoded fallback except NEFT)
 *  - UPI QR panel: static QR, copy VPA, download QR, verification notice
 *  - Bank account selector for NEFT/RTGS/IMPS/BANK_TRANSFER
 *  - Cash desk selector + cashier ID for CASH
 *
 * COMPLIANCE: QR scan alone NEVER triggers receipt verification or GL posting.
 *             Independent bank-credit verification is mandatory.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, RefreshCw, CheckCircle2, Clock, Archive,
  ChevronRight, Send, TrendingDown, DollarSign, AlertCircle,
  Copy, Download, Smartphone, Banknote, ArrowLeftRight,
  ShieldAlert, Info,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { arReceiptApi, payConfigApi, formatError } from "@/services/api";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, minimumFractionDigits: 0 }).format(minor / 100);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

// Channel categorisation for conditional form sections
function getChannelCat(method) {
  if (!method) return "OTHER";
  const m = method.toUpperCase();
  if (["UPI", "PHONEPAY"].includes(m)) return "UPI";
  if (["NEFT", "RTGS", "IMPS", "BANK_TRANSFER", "CHEQUE"].includes(m)) return "BANK";
  if (m === "CASH") return "CASH";
  return "OTHER";
}

const STATUS_CFG = {
  PENDING_VERIFICATION: { label: "Pending",   bg: "#FEF3C7", text: "#92400E", border: "#FDE68A", Icon: Clock },
  VERIFIED:             { label: "Verified",  bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE", Icon: CheckCircle2 },
  PARTIALLY_ALLOCATED:  { label: "Partial",   bg: "#F0FDF4", text: "#166534", border: "#BBF7D0", Icon: TrendingDown },
  FULLY_ALLOCATED:      { label: "Allocated", bg: "#F0FDF4", text: "#166534", border: "#BBF7D0", Icon: CheckCircle2 },
  REVERSED:             { label: "Reversed",  bg: "#F5F5F5", text: "#6B7280", border: "#E5E7EB", Icon: Archive },
  CANCELLED:            { label: "Cancelled", bg: "#FEF2F2", text: "#991B1B", border: "#FECACA", Icon: Archive },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || { label: status, bg: "#F5F5F5", text: "#666", border: "#DDD", Icon: Clock };
  const Ic = cfg.Icon;
  return (
    <span data-testid={`status-badge-${status?.toLowerCase()}`} style={{
      display: "inline-flex", alignItems: "center", gap: "4px", padding: "3px 10px",
      borderRadius: "20px", fontSize: "11px", fontWeight: 700,
      background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}`,
    }}>
      <Ic size={10} /> {cfg.label}
    </span>
  );
}

const FILTERS = [
  { label: "All",       value: "" },
  { label: "Pending",   value: "PENDING_VERIFICATION" },
  { label: "Verified",  value: "VERIFIED" },
  { label: "Allocated", value: "PARTIALLY_ALLOCATED" },
  { label: "Full",      value: "FULLY_ALLOCATED" },
  { label: "Reversed",  value: "REVERSED" },
];

const EMPTY_FORM = {
  customer_id: "", legal_entity_id: "", event_master_id: "",
  receipt_date: new Date().toISOString().slice(0, 10),
  accounting_date: new Date().toISOString().slice(0, 10),
  receipt_method: "", receipt_amount_minor: "", currency_code: "INR",
  external_reference: "", bank_account_ref: "", narration: "", internal_notes: "",
  // PAY-01B channel-specific
  payment_channel: "",
  upi_vpa: "", upi_transaction_id: "",
  cashier_id: "", cash_desk_ref: "", shift_date: "",
};

// ── UPI QR Panel ──────────────────────────────────────────────────────────────
function UPIQRPanel({ method }) {
  const [merchant, setMerchant]   = useState(null);
  const [qrData,   setQRData]     = useState(null);
  const [loading,  setLoading]    = useState(true);
  const [error,    setError]      = useState(null);
  const [copied,   setCopied]     = useState(false);

  useEffect(() => {
    if (!["UPI", "PHONEPAY"].includes(method?.toUpperCase())) return;
    setLoading(true);
    setError(null);
    let cancelled = false;

    const load = async () => {
      try {
        const merchantRes = await payConfigApi.getActiveUPIMerchant(method.toUpperCase());
        if (cancelled) return;
        const m = merchantRes.data;
        setMerchant(m);
        const qrRes = await payConfigApi.getUPIQR(m.id);
        if (cancelled) return;
        setQRData(qrRes.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "No active UPI merchant configured");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [method]);

  const handleCopy = () => {
    if (!merchant?.merchant_vpa) return;
    navigator.clipboard.writeText(merchant.merchant_vpa).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleDownload = () => {
    if (!qrData?.qr_image_base64) return;
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${qrData.qr_image_base64}`;
    link.download = `${merchant?.merchant_display_name || "upi"}-qr.png`;
    link.click();
  };

  if (loading) return (
    <div style={{ padding: "16px", background: "#F0F9FF", borderRadius: "8px", border: "1px solid #BAE6FD", textAlign: "center", color: "#0369A1", fontSize: "13px" }}>
      Loading QR code…
    </div>
  );

  if (error) return (
    <div style={{ padding: "12px 16px", background: "#FFF7ED", borderRadius: "8px", border: "1px solid #FED7AA", display: "flex", gap: "10px", alignItems: "flex-start" }}>
      <Info size={15} style={{ color: "#C2410C", marginTop: "1px", flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: "12px", fontWeight: 600, color: "#C2410C" }}>No UPI merchant configured</div>
        <div style={{ fontSize: "11px", color: "#92400E", marginTop: "2px" }}>
          Ask your Finance Admin to configure a UPI merchant in AR Master Data → UPI Merchants.
          You can still record the receipt manually below.
        </div>
      </div>
    </div>
  );

  const isDemo = qrData?.qr_activation_status === "DEMO_CONFIGURATION";

  return (
    <div data-testid="upi-qr-panel" style={{ background: "#F8FAFF", border: "1px solid #C7D7FE", borderRadius: "10px", padding: "16px" }}>
      {/* Demo warning banner */}
      {isDemo && (
        <div style={{ display: "flex", gap: "8px", alignItems: "flex-start", padding: "8px 12px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: "6px", marginBottom: "12px" }}>
          <ShieldAlert size={14} style={{ color: "#92400E", marginTop: "1px", flexShrink: 0 }} />
          <div style={{ fontSize: "11px", color: "#92400E" }}>
            <strong>DEMO CONFIGURATION</strong> — This QR uses a test VPA and is for UAT only.
            Finance Owner must verify and authorize a real VPA before production use.
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "16px", alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* QR Image */}
        {qrData?.qr_image_base64 && (
          <div style={{ flexShrink: 0 }}>
            <img
              data-testid="upi-qr-image"
              src={`data:image/png;base64,${qrData.qr_image_base64}`}
              alt="UPI QR Code"
              style={{ width: 140, height: 140, border: "1px solid #E5E7EB", borderRadius: "6px", display: "block" }}
            />
          </div>
        )}

        {/* Merchant Info + Actions */}
        <div style={{ flex: 1, minWidth: "180px" }}>
          <div style={{ fontSize: "13px", fontWeight: 700, color: "#111827", marginBottom: "4px" }}>
            {merchant?.merchant_display_name}
          </div>
          <div style={{ fontSize: "12px", color: "#6B7280", marginBottom: "10px", fontFamily: "monospace" }}>
            {merchant?.merchant_vpa}
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button
              data-testid="copy-vpa-btn"
              type="button"
              onClick={handleCopy}
              style={{ display: "flex", alignItems: "center", gap: "5px", padding: "5px 10px", background: copied ? "#F0FDF4" : "#fff", border: `1px solid ${copied ? "#86EFAC" : "#D1D5DB"}`, borderRadius: "6px", cursor: "pointer", fontSize: "12px", color: copied ? "#166534" : "#374151" }}>
              <Copy size={12} /> {copied ? "Copied!" : "Copy UPI ID"}
            </button>
            {qrData?.qr_image_base64 && (
              <button
                data-testid="download-qr-btn"
                type="button"
                onClick={handleDownload}
                style={{ display: "flex", alignItems: "center", gap: "5px", padding: "5px 10px", background: "#fff", border: "1px solid #D1D5DB", borderRadius: "6px", cursor: "pointer", fontSize: "12px", color: "#374151" }}>
                <Download size={12} /> Download QR
              </button>
            )}
          </div>

          {/* Verification notice */}
          <div style={{ marginTop: "10px", padding: "8px 10px", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: "6px" }}>
            <div style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
              <AlertCircle size={12} style={{ color: "#92400E", marginTop: "1px", flexShrink: 0 }} />
              <div style={{ fontSize: "11px", color: "#78350F", lineHeight: "1.4" }}>
                QR scan does <strong>not</strong> confirm payment. You must independently verify
                the bank credit before marking this receipt as Verified.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ARReceiptWorkspace() {
  const { user } = useAuth();
  const navigate  = useNavigate();

  const [receipts,      setReceipts]      = useState([]);
  const [total,         setTotal]         = useState(0);
  const [counts,        setCounts]        = useState({});
  const [loading,       setLoading]       = useState(true);
  const [filter,        setFilter]        = useState("");
  const [page,          setPage]          = useState(1);

  // Create-modal deps
  const [showCreate,    setShowCreate]    = useState(false);
  const [customers,     setCustomers]     = useState([]);
  const [legalEntities, setLEs]           = useState([]);
  const [methods,       setMethods]       = useState([]);
  const [bankAccounts,  setBankAccounts]  = useState([]);
  const [form,          setForm]          = useState(EMPTY_FORM);
  const [creating,      setCreating]      = useState(false);

  const PAGE_SIZE = 25;
  const hasCap    = (cap) => user?.capabilities?.includes(cap);
  const canRecord = hasCap("ar.receipt.record");

  // ── Load receipts ──────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (filter) params.status = filter;
      const res = await arReceiptApi.list(params);
      setReceipts(res.data.receipts || []);
      setTotal(res.data.total || 0);
      setCounts(res.data.counts || {});
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => { load(); }, [load]);

  // ── Load modal deps ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!showCreate) return;
    const loadDeps = async () => {
      try {
        const [custRes, leRes, methodRes, bankRes] = await Promise.all([
          axios.get(`${API}/api/customers`, { withCredentials: true }),
          axios.get(`${API}/api/organization/legal-entities`, { withCredentials: true }),
          arReceiptApi.getMDMConfig("RECEIPT_METHOD"),
          payConfigApi.listBankAccounts(),
        ]);
        setCustomers(Array.isArray(custRes.data) ? custRes.data : (custRes.data?.items || []));
        setLEs(Array.isArray(leRes.data) ? leRes.data : (leRes.data?.items || []));
        const mdmMethods = methodRes.data?.items || [];
        setMethods(mdmMethods);
        setBankAccounts(bankRes.data?.accounts || []);
        // Set default receipt method to first active MDM method
        if (mdmMethods.length > 0 && !form.receipt_method) {
          setForm(f => ({ ...f, receipt_method: mdmMethods[0].item_code }));
        }
      } catch { /* non-blocking — form degrades gracefully */ }
    };
    loadDeps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCreate]);

  // ── Create receipt ─────────────────────────────────────────────────────────
  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.legal_entity_id || !form.receipt_amount_minor || !form.external_reference) {
      toast.error("Customer, legal entity, amount and reference are required");
      return;
    }
    setCreating(true);
    try {
      const channelCat = getChannelCat(form.receipt_method);
      const payload = {
        customer_id:          form.customer_id,
        legal_entity_id:      form.legal_entity_id,
        event_master_id:      form.event_master_id || null,
        receipt_date:         form.receipt_date,
        accounting_date:      form.accounting_date,
        receipt_method:       form.receipt_method,
        receipt_amount_minor: Math.round(parseFloat(form.receipt_amount_minor) * 100),
        currency_code:        form.currency_code,
        external_reference:   form.external_reference,
        bank_account_ref:     form.bank_account_ref || null,
        narration:            form.narration || null,
        internal_notes:       form.internal_notes || null,
        // PAY-01B channel-specific
        payment_channel:      form.receipt_method || null,
        upi_vpa:              channelCat === "UPI" ? (form.upi_vpa || null) : null,
        upi_transaction_id:   channelCat === "UPI" ? (form.upi_transaction_id || null) : null,
        cashier_id:           channelCat === "CASH" ? (form.cashier_id || null) : null,
        cash_desk_ref:        channelCat === "CASH" ? (form.cash_desk_ref || null) : null,
        shift_date:           channelCat === "CASH" ? (form.shift_date || null) : null,
      };
      const res = await arReceiptApi.create(payload);
      toast.success(`Receipt ${res.data.receipt_number} recorded (Pending Verification)`);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      navigate(`/ar/receipts/${res.data.id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setCreating(false);
    }
  };

  const totalPages  = Math.ceil(total / PAGE_SIZE);
  const channelCat  = getChannelCat(form.receipt_method);
  const cashDesks   = bankAccounts.filter(a => a.account_type === "CASH_DESK");
  const bankOnlyAcc = bankAccounts.filter(a => a.account_type !== "CASH_DESK");

  const countItems = [
    { key: "PENDING_VERIFICATION", label: "Pending",  color: "#92400E", bg: "#FEF3C7" },
    { key: "VERIFIED",             label: "Verified", color: "#1D4ED8", bg: "#EFF6FF" },
    { key: "PARTIALLY_ALLOCATED",  label: "Partial",  color: "#166534", bg: "#F0FDF4" },
    { key: "FULLY_ALLOCATED",      label: "Full",     color: "#166534", bg: "#F0FDF4" },
    { key: "REVERSED",             label: "Reversed", color: "#6B7280", bg: "#F5F5F5" },
  ];

  return (
    <div data-testid="ar-receipt-workspace" style={{ padding: "24px", maxWidth: "1280px", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 style={{ fontSize: "22px", fontWeight: 700, color: "#111827", margin: 0 }}>Customer Receipts</h1>
          <p style={{ color: "#6B7280", fontSize: "13px", marginTop: "4px" }}>ERP-FIN-02 · Gate AR-02 · PAY-01B Manual Collections</p>
        </div>
        <div style={{ display: "flex", gap: "8px" }}>
          {canRecord && (
            <button data-testid="create-receipt-btn" onClick={() => setShowCreate(true)}
              style={{ display: "flex", alignItems: "center", gap: "6px", background: "#0176D3", color: "#fff", border: "none", padding: "8px 16px", borderRadius: "6px", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
              <Plus size={15} /> Record Receipt
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: "12px", marginBottom: "20px" }}>
        {countItems.map(({ key, label, color, bg }) => (
          <div key={key} data-testid={`count-card-${key.toLowerCase()}`}
            onClick={() => { setFilter(filter === key ? "" : key); setPage(1); }}
            style={{ background: filter === key ? bg : "#fff", border: `1px solid ${filter === key ? color + "50" : "#E5E7EB"}`, borderRadius: "8px", padding: "14px", cursor: "pointer" }}>
            <div style={{ fontSize: "22px", fontWeight: 700, color }}>{counts[key] ?? 0}</div>
            <div style={{ fontSize: "11px", color: "#6B7280", marginTop: "2px" }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: "4px", background: "#F3F4F6", borderRadius: "8px", padding: "4px" }}>
          {FILTERS.map(f => (
            <button key={f.value} data-testid={`filter-tab-${f.value || "all"}`}
              onClick={() => { setFilter(f.value); setPage(1); }}
              style={{ padding: "5px 12px", borderRadius: "6px", border: "none", background: filter === f.value ? "#fff" : "transparent", color: filter === f.value ? "#111827" : "#6B7280", fontWeight: filter === f.value ? 600 : 400, fontSize: "12px", cursor: "pointer", boxShadow: filter === f.value ? "0 1px 3px rgba(0,0,0,.1)" : "none" }}>
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: "auto" }}>
          <button data-testid="refresh-btn" onClick={load}
            style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "6px", padding: "7px 10px", cursor: "pointer", color: "#6B7280" }}>
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Receipt List */}
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "10px", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "48px", textAlign: "center", color: "#6B7280" }}>Loading receipts…</div>
        ) : receipts.length === 0 ? (
          <div style={{ padding: "48px", textAlign: "center", color: "#9CA3AF" }}>
            <DollarSign size={36} style={{ marginBottom: "12px", opacity: 0.4 }} />
            <div style={{ fontWeight: 600, marginBottom: "4px" }}>No receipts found</div>
            <div style={{ fontSize: "13px" }}>{canRecord ? "Record a receipt to get started." : "No receipts match the current filter."}</div>
          </div>
        ) : (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
                  {["Receipt #", "Customer", "Date", "Method", "Amount", "Verified", "Applied", "Status", ""].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: ["Amount","Verified","Applied"].includes(h) ? "right" : "left", fontSize: "11px", fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {receipts.map((r, idx) => (
                  <tr key={r.id} data-testid={`receipt-row-${r.id}`}
                    onClick={() => navigate(`/ar/receipts/${r.id}`)}
                    style={{ borderBottom: idx < receipts.length - 1 ? "1px solid #F3F4F6" : "none", cursor: "pointer" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={{ padding: "11px 14px", fontSize: "13px", fontWeight: 600, color: "#0176D3" }}>{r.receipt_number || "—"}</td>
                    <td style={{ padding: "11px 14px" }}>
                      <div style={{ fontSize: "13px", fontWeight: 500, color: "#111827" }}>{r.customer_name || "—"}</div>
                      {r.event_name && <div style={{ fontSize: "11px", color: "#6B7280" }}>{r.event_name}</div>}
                    </td>
                    <td style={{ padding: "11px 14px", fontSize: "13px", color: "#374151" }}>{fmtDate(r.receipt_date)}</td>
                    <td style={{ padding: "11px 14px" }}>
                      <ChannelBadge method={r.receipt_method} />
                    </td>
                    <td style={{ padding: "11px 14px", fontSize: "13px", fontWeight: 600, color: "#111827", textAlign: "right" }}>{fmt(r.receipt_amount_minor, r.currency_code)}</td>
                    <td style={{ padding: "11px 14px", fontSize: "13px", color: "#374151", textAlign: "right" }}>{fmt(r.confirmed_amount_minor, r.currency_code)}</td>
                    <td style={{ padding: "11px 14px", fontSize: "13px", color: "#374151", textAlign: "right" }}>{fmt(r.applied_amount_minor, r.currency_code)}</td>
                    <td style={{ padding: "11px 14px" }}><StatusBadge status={r.status} /></td>
                    <td style={{ padding: "11px 14px", color: "#9CA3AF" }}><ChevronRight size={14} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid #F3F4F6", fontSize: "13px" }}>
                <span style={{ color: "#6B7280" }}>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}</span>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button disabled={page === 1} onClick={() => setPage(p => p - 1)} style={{ padding: "4px 12px", border: "1px solid #E5E7EB", borderRadius: "6px", cursor: page === 1 ? "not-allowed" : "pointer", background: "#fff", opacity: page === 1 ? 0.5 : 1 }}>Previous</button>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ padding: "4px 12px", border: "1px solid #E5E7EB", borderRadius: "6px", cursor: page >= totalPages ? "not-allowed" : "pointer", background: "#fff", opacity: page >= totalPages ? 0.5 : 1 }}>Next</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Create Receipt Modal ────────────────────────────────────────────── */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
          <div data-testid="create-receipt-modal" style={{ background: "#fff", borderRadius: "12px", padding: "28px", width: "100%", maxWidth: "620px", maxHeight: "92vh", overflowY: "auto" }}>
            <h2 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "4px", color: "#111827" }}>Record Customer Receipt</h2>
            <p style={{ fontSize: "12px", color: "#6B7280", marginBottom: "20px" }}>PAY-01B · Manual Collections · Status: Pending Verification</p>

            <form onSubmit={handleCreate}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>

                {/* Customer */}
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>Customer *</label>
                  <select data-testid="customer-select" required value={form.customer_id}
                    onChange={e => setForm(f => ({ ...f, customer_id: e.target.value }))}
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }}>
                    <option value="">— Select Customer —</option>
                    {customers.map(c => <option key={c.id || c._id} value={c.id || c._id}>{c.name || c.customer_name}</option>)}
                  </select>
                </div>

                {/* Legal Entity */}
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>Legal Entity *</label>
                  <select data-testid="legal-entity-select" required value={form.legal_entity_id}
                    onChange={e => setForm(f => ({ ...f, legal_entity_id: e.target.value }))}
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }}>
                    <option value="">— Select Legal Entity —</option>
                    {legalEntities.map(le => <option key={le._id || le.id} value={le._id || le.id}>{le.name}</option>)}
                  </select>
                </div>

                {/* Dates */}
                <div>
                  <label style={lbl}>Receipt Date *</label>
                  <input data-testid="receipt-date-input" type="date" required value={form.receipt_date}
                    onChange={e => setForm(f => ({ ...f, receipt_date: e.target.value }))}
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                </div>
                <div>
                  <label style={lbl}>Accounting Date *</label>
                  <input data-testid="accounting-date-input" type="date" required value={form.accounting_date}
                    onChange={e => setForm(f => ({ ...f, accounting_date: e.target.value }))}
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                </div>

                {/* Payment Method */}
                <div>
                  <label style={lbl}>Payment Method *</label>
                  <select data-testid="method-select" value={form.receipt_method}
                    onChange={e => setForm(f => ({ ...f, receipt_method: e.target.value, payment_channel: e.target.value, upi_vpa: "", upi_transaction_id: "", cashier_id: "", cash_desk_ref: "", bank_account_ref: "" }))}
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }}>
                    {methods.length === 0
                      ? ["NEFT","RTGS","IMPS","BANK_TRANSFER","UPI","PHONEPAY","CASH","CHEQUE"].map(m =>
                          <option key={m} value={m}>{m}</option>)
                      : methods.map(m => <option key={m.item_code} value={m.item_code}>{m.display_name || m.item_name || m.item_code}</option>)
                    }
                  </select>
                </div>

                {/* Amount */}
                <div>
                  <label style={lbl}>Amount (₹) *</label>
                  <input data-testid="amount-input" type="number" min="0.01" step="0.01" required
                    value={form.receipt_amount_minor}
                    onChange={e => setForm(f => ({ ...f, receipt_amount_minor: e.target.value }))}
                    placeholder="0.00"
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                  <p style={{ fontSize: "11px", color: "#6B7280", margin: "2px 0 0" }}>Enter in rupees (e.g. 50000 = ₹50,000)</p>
                </div>

                {/* Universal Reference */}
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>
                    {channelCat === "CASH" ? "Cash Receipt Reference *" :
                     channelCat === "UPI"  ? "Transaction ID / Reference *" :
                                            "Bank Reference / UTR / Cheque # *"}
                  </label>
                  <input data-testid="ext-ref-input" type="text" required
                    value={form.external_reference}
                    onChange={e => setForm(f => ({ ...f, external_reference: e.target.value }))}
                    placeholder={
                      channelCat === "CASH" ? "Cashier receipt / bill number" :
                      channelCat === "UPI"  ? "UPI Transaction ID / UTR" :
                                             "UTR number / cheque number / transaction ID"
                    }
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                  <p style={{ fontSize: "11px", color: "#6B7280", margin: "2px 0 0" }}>Must be unique per tenant. Duplicate UTR will be rejected.</p>
                </div>

                {/* ── Channel-specific: UPI / PhonePe ── */}
                {channelCat === "UPI" && (
                  <>
                    {/* QR Panel */}
                    <div style={{ gridColumn: "1/-1" }}>
                      <label style={{ ...lbl, marginBottom: "8px" }}>Merchant QR Code</label>
                      <UPIQRPanel method={form.receipt_method} />
                    </div>

                    {/* Payer VPA */}
                    <div style={{ gridColumn: "1/-1" }}>
                      <label style={lbl}>Payer UPI VPA (optional)</label>
                      <input data-testid="upi-vpa-input" type="text"
                        value={form.upi_vpa}
                        onChange={e => setForm(f => ({ ...f, upi_vpa: e.target.value }))}
                        placeholder="customer@bankhandle (payer's UPI ID)"
                        style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                      <p style={{ fontSize: "11px", color: "#6B7280", margin: "2px 0 0" }}>Payer's UPI Virtual Payment Address — for reconciliation only.</p>
                    </div>

                    {/* UPI Transaction ID */}
                    <div style={{ gridColumn: "1/-1" }}>
                      <label style={lbl}>UPI Transaction ID / UTR (optional)</label>
                      <input data-testid="upi-txn-input" type="text"
                        value={form.upi_transaction_id}
                        onChange={e => setForm(f => ({ ...f, upi_transaction_id: e.target.value }))}
                        placeholder="e.g. 412345678901 (12-digit UPI ref)"
                        style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                    </div>
                  </>
                )}

                {/* ── Channel-specific: Bank Transfer ── */}
                {channelCat === "BANK" && bankOnlyAcc.length > 0 && (
                  <div style={{ gridColumn: "1/-1" }}>
                    <label style={lbl}>Credit-to Bank Account (optional)</label>
                    <select data-testid="bank-account-select"
                      value={form.bank_account_ref}
                      onChange={e => setForm(f => ({ ...f, bank_account_ref: e.target.value }))}
                      style={{ ...inp, width: "100%", boxSizing: "border-box" }}>
                      <option value="">— Select company bank account —</option>
                      {bankOnlyAcc.filter(a => a.is_active).map(a => (
                        <option key={a.id} value={a.id}>
                          {a.account_name} · {a.bank_name} · {a.account_number_masked}
                        </option>
                      ))}
                    </select>
                    <p style={{ fontSize: "11px", color: "#6B7280", margin: "2px 0 0" }}>Select the company bank account that received this credit.</p>
                  </div>
                )}

                {/* ── Channel-specific: Cash ── */}
                {channelCat === "CASH" && (
                  <>
                    {cashDesks.length > 0 && (
                      <div style={{ gridColumn: "1/-1" }}>
                        <label style={lbl}>Cash Desk / Till</label>
                        <select data-testid="cash-desk-select"
                          value={form.cash_desk_ref}
                          onChange={e => setForm(f => ({ ...f, cash_desk_ref: e.target.value }))}
                          style={{ ...inp, width: "100%", boxSizing: "border-box" }}>
                          <option value="">— Select cash desk —</option>
                          {cashDesks.filter(a => a.is_active).map(a => (
                            <option key={a.id} value={a.id}>
                              {a.account_name} {a.location ? `· ${a.location}` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label style={lbl}>Cashier ID</label>
                      <input data-testid="cashier-id-input" type="text"
                        value={form.cashier_id}
                        onChange={e => setForm(f => ({ ...f, cashier_id: e.target.value }))}
                        placeholder="Staff ID / name of cashier"
                        style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                    </div>
                    <div>
                      <label style={lbl}>Shift Date</label>
                      <input data-testid="shift-date-input" type="date"
                        value={form.shift_date}
                        onChange={e => setForm(f => ({ ...f, shift_date: e.target.value }))}
                        style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                    </div>

                    {/* Cash verification notice */}
                    <div style={{ gridColumn: "1/-1", padding: "10px 12px", background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: "6px" }}>
                      <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                        <Info size={13} style={{ color: "#166534", marginTop: "1px", flexShrink: 0 }} />
                        <div style={{ fontSize: "11px", color: "#166534" }}>
                          Cash receipts remain <strong>Pending Verification</strong> until a Finance Approver
                          counts and confirms the physical cash. Do not credit the customer account before verification.
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Narration */}
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={lbl}>Narration (optional)</label>
                  <input type="text"
                    value={form.narration}
                    onChange={e => setForm(f => ({ ...f, narration: e.target.value }))}
                    placeholder="Bank narration / payment description"
                    style={{ ...inp, width: "100%", boxSizing: "border-box" }} />
                </div>

              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button type="button" onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); }}
                  style={{ padding: "8px 18px", border: "1px solid #D1D5DB", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "13px" }}>
                  Cancel
                </button>
                <button type="submit" data-testid="create-receipt-submit" disabled={creating}
                  style={{ padding: "8px 18px", background: creating ? "#93C5FD" : "#0176D3", color: "#fff", border: "none", borderRadius: "6px", cursor: creating ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}>
                  {creating ? "Recording…" : "Record Receipt"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Channel badge for list ────────────────────────────────────────────────────
function ChannelBadge({ method }) {
  if (!method) return <span style={{ color: "#9CA3AF", fontSize: "12px" }}>—</span>;
  const cat = getChannelCat(method);
  const Icon = cat === "UPI" ? Smartphone : cat === "CASH" ? Banknote : ArrowLeftRight;
  const color = cat === "UPI" ? "#7C3AED" : cat === "CASH" ? "#16A34A" : "#1D4ED8";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", color }}>
      <Icon size={12} /> {method}
    </span>
  );
}

const lbl = { display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" };
const inp = { padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "13px" };
